import { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FiSend,
  FiPaperclip,
  FiFileText,
  FiStar,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.dhoorbin.com";

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}
function getSessionId() {
  let id = localStorage.getItem("botSessionId");
  if (!id) {
    id = newId();
    localStorage.setItem("botSessionId", id);
  }
  return id;
}
const money = (v) => `৳${Number(v || 0).toLocaleString()}`;

/* ---------- scrollable carousel with arrows ---------- */
function Carousel({ children }) {
  const ref = useRef(null);
  const scroll = (dir) =>
    ref.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  return (
    <div className="relative">
      <div
        ref={ref}
        className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 snap-x"
      >
        {children}
      </div>
      <button
        onClick={() => scroll(-1)}
        className="absolute -left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white shadow border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
      >
        <FiChevronLeft />
      </button>
      <button
        onClick={() => scroll(1)}
        className="absolute -right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white shadow border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
      >
        <FiChevronRight />
      </button>
    </div>
  );
}

/* ---------- hotel / room carousel ---------- */
function CardsMessage({ payload, onViewHotel, onViewRoom }) {
  const tool = payload?.tool;
  const data = payload?.data || {};

  if (tool === "suggest_hotels") {
    const hotels = data.hotels || [];
    if (!hotels.length)
      return <p className="text-sm text-gray-500">No matching hotels found.</p>;
    return (
      <Carousel>
        {hotels.map((h) => (
          <div
            key={h.id}
            className="snap-start shrink-0 w-52 rounded-xl border border-gray-200 bg-white overflow-hidden"
          >
            <div className="h-28 bg-gray-100">
              {h.hotelLogo && (
                <img
                  src={h.hotelLogo}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="p-2.5 space-y-1">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {h.hotelName}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {[h.city, h.division].filter(Boolean).join(", ")}
              </p>
              <div className="flex items-center justify-between pt-1">
                {h.propertyStar ? (
                  <span className="inline-flex items-center gap-0.5 text-xs text-amber-500">
                    <FiStar className="fill-amber-400 stroke-amber-400" />{" "}
                    {h.propertyStar}
                  </span>
                ) : (
                  <span />
                )}
                {h.fromPrice ? (
                  <span className="text-xs font-semibold text-gray-700">
                    {money(h.fromPrice)}+
                  </span>
                ) : null}
              </div>
              <button
                onClick={() => onViewHotel(h)}
                className="w-full mt-1.5 text-xs font-medium py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                View rooms
              </button>
            </div>
          </div>
        ))}
      </Carousel>
    );
  }

  if (tool === "get_hotel_details") {
    const hotel = data.hotel;
    if (!hotel) return null;
    return (
      <div className="space-y-2">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            {hotel.hotelName}
          </p>
          <p className="text-xs text-gray-500">
            {[hotel.city, hotel.division].filter(Boolean).join(", ")}
          </p>
        </div>
        <Carousel>
          {(hotel.rooms || []).map((r) => (
            <div
              key={r.id}
              className="snap-start shrink-0 w-44 rounded-xl border border-gray-200 bg-white overflow-hidden"
            >
              <div className="h-24 bg-gray-100">
                {r.images?.[0] && (
                  <img
                    src={r.images[0]}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-2.5 space-y-1">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {r.roomName}
                </p>
                <p className="text-[11px] text-gray-500">
                  {r.adults} adults
                  {r.children ? `, ${r.children} children` : ""}
                </p>
                <button
                  onClick={() => onViewRoom({ room: r, hotel })}
                  className="w-full mt-1 text-xs font-medium py-1.5 rounded-lg border border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  View details
                </button>
              </div>
            </div>
          ))}
        </Carousel>
      </div>
    );
  }
  return null;
}

/* ---------- room details modal (table) -> Yes/No -> book ---------- */
function RoomDetailsModal({ ctx, onClose, onBook }) {
  const { room, hotel } = ctx;
  const [avail, setAvail] = useState([]);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/bot/room/${room.id}/availability`)
      .then((r) => r.json())
      .then((d) => active && setAvail(d.availability || []))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [room.id]);

  const rows = [
    ["Room Type", room.roomType],
    ["Bed Size", room.bedSize],
    ["Beds", room.beds],
    ["Bathrooms", room.bathrooms],
    ["Room Size", room.roomSize ? `${room.roomSize}` : null],
    ["Room View", room.roomView],
    ["Direction", room.roomDirection],
    ["Max Adults", room.adults],
    ["Max Children", room.children],
    ["Breakfast", room.isBreakFastIncluded ? "Included" : "Not included"],
    ["Refundable", room.isRefundable ? "Yes" : "No"],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto scrollbar-hide">
        {room.images?.[0] && (
          <div className="h-40 bg-gray-100">
            <img
              src={room.images[0]}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-5 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-semibold text-gray-800">{room.roomName}</p>
              <p className="text-xs text-gray-500">{hotel.hotelName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX />
            </button>
          </div>

          {room.description && (
            <p className="text-xs text-gray-600 leading-relaxed">
              {room.description}
            </p>
          )}

          <table className="w-full text-sm">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 text-gray-500">{k}</td>
                  <td className="py-2 text-right font-medium text-gray-800">
                    {v}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {avail.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">
                Date-wise price
              </p>
              <div className="max-h-40 overflow-y-auto scrollbar-hide rounded-lg border border-gray-100">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-right px-2 py-1.5">Price</th>
                      <th className="text-right px-2 py-1.5">Discount</th>
                      <th className="text-right px-2 py-1.5">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {avail.map((a) => (
                      <tr key={a.date} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 text-gray-700 text-nowrap">
                          {String(a.date).slice(0, 10)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-gray-500 line-through">
                          {money(a.price)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-green-600">
                          {a.discount ? `${a.discount}%` : "-"}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-gray-800">
                          {money(a.discountedPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {room.cancelationPolicy && (
            <div>
              <p className="text-xs font-semibold text-gray-700">
                Cancellation Policy
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                {room.cancelationPolicy}
              </p>
            </div>
          )}

          <div className="pt-2 border-t">
            <p className="text-sm font-medium text-gray-800 mb-2">
              Do you want to book this room?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  onBook({
                    hotelId: hotel.id,
                    hotelName: hotel.hotelName,
                    roomId: room.id,
                    roomName: room.roomName,
                  })
                }
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
              >
                Yes, book
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-50"
              >
                No
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- booking confirmation bubble ---------- */
function BookingMessage({ data }) {
  return (
    <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm">
      <p className="font-semibold text-green-800">Booking request placed</p>
      <p className="text-gray-700 mt-1">
        Invoice <strong>{data.invoiceId}</strong> · {data.nights} night(s)
      </p>
      <p className="text-gray-700">Total {money(data.totalAmount)}</p>
      <p className="text-xs text-amber-700 mt-1.5">
        Not confirmed yet. An unpaid invoice has been emailed to you. Pay to
        confirm.
      </p>
    </div>
  );
}

/* ---------- booking form modal ---------- */
function BookModal({ ctx, onClose, onSubmit }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    checkIn: "",
    checkOut: "",
  });
  const set = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = () => {
    if (!form.fullName || !form.phoneNumber || !form.checkIn || !form.checkOut)
      return;
    onSubmit({
      hotelId: ctx.hotelId,
      checkIn: form.checkIn,
      checkOut: form.checkOut,
      rooms: [{ roomId: ctx.roomId }],
      customer: {
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
        <div className="flex justify-between items-center">
          <p className="font-semibold text-gray-800">
            Book {ctx.roomName || ctx.hotelName}
          </p>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            name="fullName"
            value={form.fullName}
            onChange={set}
            placeholder="Full name"
            className="col-span-2 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <input
            name="email"
            value={form.email}
            onChange={set}
            placeholder="Email"
            className="col-span-2 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <input
            name="phoneNumber"
            value={form.phoneNumber}
            onChange={set}
            placeholder="Phone"
            className="col-span-2 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <div>
            <label className="text-xs text-gray-500">Check In</label>
            <input
              type="date"
              name="checkIn"
              value={form.checkIn}
              onChange={set}
              className="w-full border rounded-lg px-2 py-2 text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Check Out</label>
            <input
              type="date"
              name="checkOut"
              value={form.checkOut}
              onChange={set}
              className="w-full border rounded-lg px-2 py-2 text-sm focus:outline-none"
            />
          </div>
        </div>
        <p className="text-[11px] text-gray-400">
          This creates a booking request and emails an unpaid invoice. Payment
          confirms it.
        </p>
        <button
          onClick={submit}
          className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          Place booking request
        </button>
      </div>
    </div>
  );
}

/* ---------- a single message bubble ---------- */
function Bubble({ m, onViewHotel, onViewRoom }) {
  const mine = m.role === "user";
  const system = m.role === "system";
  const admin = m.role === "admin";

  if (system) {
    return (
      <div className="flex justify-center">
        <span className="text-[11px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
          {m.content}
        </span>
      </div>
    );
  }

  if (m.type === "cards") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[92%] w-full">
          <CardsMessage
            payload={m.content}
            onViewHotel={onViewHotel}
            onViewRoom={onViewRoom}
          />
        </div>
      </div>
    );
  }

  if (m.type === "booking") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%]">
          <BookingMessage data={m.content} />
        </div>
      </div>
    );
  }

  if (m.type === "file") {
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div
          className={`max-w-[80%] rounded-2xl overflow-hidden border ${mine ? "border-blue-200" : "border-gray-200"}`}
        >
          {m.fileType === "image" ? (
            <img src={m.fileLink} alt="" className="max-h-56 object-cover" />
          ) : (
            <a
              href={m.fileLink}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2.5 bg-white text-sm text-blue-600"
            >
              <FiFileText /> {m.content || "Attachment"}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm shadow-sm ${
          mine
            ? "bg-blue-600 text-white rounded-br-md"
            : admin
              ? "bg-emerald-600 text-white rounded-bl-md"
              : "bg-white text-gray-800 border border-gray-200 rounded-bl-md"
        }`}
      >
        {mine ? (
          <span className="whitespace-pre-wrap">{m.content}</span>
        ) : (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {m.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================================
   Main component
   ===================================================================== */
export default function CustomerBot({
  isAdmin = false,
  adminId,
  sessionId: adminSessionId,
}) {
  const [sessionId] = useState(() =>
    isAdmin ? adminSessionId : getSessionId(),
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [bookCtx, setBookCtx] = useState(null);
  const [detailsCtx, setDetailsCtx] = useState(null);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  useEffect(() => {
    if (!sessionId) return;
    fetch(`${API_BASE}/bot/history/${sessionId}`)
      .then((r) => r.json())
      .then((d) => {
        setMessages(d.messages || []);
        if (d.session) setAiEnabled(!!d.session.aiEnabled);
      })
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    if (isAdmin) socket.emit("admin:join", { sessionId, adminId });
    else socket.emit("customer:init", { sessionId });

    socket.on("session:ready", (d) => setAiEnabled(!!d.aiEnabled));
    socket.on("ai:toggled", (d) => setAiEnabled(!!d.aiEnabled));
    socket.on("bot:typing", (v) => setTyping(!!v));
    socket.on("bot:message", (msg) => {
      setMessages((prev) =>
        prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
      );
    });
    socket.on("bot:error", (e) => console.error("bot error:", e));

    return () => socket.disconnect();
  }, [sessionId, isAdmin, adminId]);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    if (isAdmin)
      socketRef.current?.emit("admin:message", { sessionId, adminId, text });
    else socketRef.current?.emit("customer:message", { sessionId, text });
    setInput("");
  }, [input, isAdmin, sessionId, adminId]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API_BASE}/bot/upload`, {
        method: "POST",
        body: fd,
      });
      const d = await res.json();
      if (d.fileLink) {
        socketRef.current?.emit("customer:file", {
          sessionId,
          fileLink: d.fileLink,
          fileType: d.fileType,
          name: d.name,
        });
      }
    } catch (_) {}
    e.target.value = "";
  };

  const toggleAI = (enabled) => {
    socketRef.current?.emit("admin:toggleAI", { sessionId, adminId, enabled });
  };

  const submitBooking = (payload) => {
    socketRef.current?.emit("customer:book", { sessionId, ...payload });
    setBookCtx(null);
  };

  const viewHotel = (h) => {
    socketRef.current?.emit("customer:viewHotel", {
      sessionId,
      hotelId: h.hotelId || h.id,
    });
  };

  // open details table -> on Yes, open the booking form
  const openBookFromDetails = (ctx) => {
    setDetailsCtx(null);
    setBookCtx(ctx);
  };

  return (
    <div className="flex flex-col w-full h-[calc(100lvh-120px)] border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div>
          <p className="font-semibold text-sm">Dhoorbin Assistant</p>
          <p className="text-[11px] text-white/60">
            {aiEnabled ? "AI is helping" : "A support agent is here"}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-white/70">AI</span>
            <button
              onClick={() => toggleAI(!aiEnabled)}
              className={`relative w-10 h-5 rounded-full transition ${aiEnabled ? "bg-emerald-400" : "bg-white/30"}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition ${aiEnabled ? "translate-x-5" : ""}`}
              />
            </button>
          </div>
        )}
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto scrollbar-hide px-3 py-4 space-y-3 bg-gray-50">
        {messages.length === 0 && !isAdmin && (
          <div className="text-center text-sm text-gray-400 mt-8 px-6">
            Tell me where you want to travel, your dates and how many guests,
            and I'll find you a hotel. 🏝️
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble
            key={m.id || i}
            m={m}
            onViewHotel={viewHotel}
            onViewRoom={setDetailsCtx}
          />
        ))}
        {typing && (
          <div className="flex justify-start">
            <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-gray-200">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* input */}
      <div className="border-t border-gray-200 p-3 bg-white">
        <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-200">
          {!isAdmin && (
            <>
              <button
                onClick={() => fileRef.current?.click()}
                className="shrink-0 w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 flex items-center justify-center"
              >
                <FiPaperclip />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFile}
                className="hidden"
              />
            </>
          )}
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={isAdmin ? "Reply to customer..." : "Type a message..."}
            className="flex-1 bg-transparent resize-none text-sm outline-none max-h-32 py-1.5"
          />
          <button
            onClick={send}
            disabled={!input.trim()}
            className="shrink-0 w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-40"
          >
            <FiSend />
          </button>
        </div>
      </div>

      {detailsCtx && (
        <RoomDetailsModal
          ctx={detailsCtx}
          onClose={() => setDetailsCtx(null)}
          onBook={openBookFromDetails}
        />
      )}
      {bookCtx && (
        <BookModal
          ctx={bookCtx}
          onClose={() => setBookCtx(null)}
          onSubmit={submitBooking}
        />
      )}
    </div>
  );
}
